import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../hooks/useAppState';
import { getTheme } from '../lib/theme';
import { determinarObligaciones } from '../lib/scoring';

export default function EvidenciaPage() {
  const { state } = useAppState();
  const navigate = useNavigate();
  const t = getTheme(state.darkMode);
  const dark = state.darkMode;
  const centro = state.centrosTrabajo[state.centroActualIdx];
  if (!centro) return null;

  const trabajadores = centro.trabajadores || [];
  const cuestionarios = centro.cuestionariosCompletos || {};
  const consentimientos = centro.consentimientos || {};
  const fichas = centro.fichas || {};
  const obligaciones = determinarObligaciones(centro.numTrabajadores);
  const pi = centro.programaIntervencion || {};

  const counts = useMemo(() => {
    let gi = 0, gii = 0, giii = 0;
    trabajadores.forEach(tw => {
      if (cuestionarios[`${tw.id}_guiaI`]) gi++;
      if (cuestionarios[`${tw.id}_guiaII`]) gii++;
      if (cuestionarios[`${tw.id}_guiaIII`]) giii++;
    });
    return { gi, gii, giii, consent: Object.keys(consentimientos).length, fichas: Object.keys(fichas).length };
  }, [trabajadores, cuestionarios, consentimientos, fichas]);

  const docs = [];

  // ── Todos los centros (5.1, 5.4, 5.5, 5.7, 8.1, 8.2) ──
  docs.push(
    { numeral: '5.1', titulo: 'Política de prevención de riesgos psicosociales', estado: state.politicaData.guardado, desc: 'Documento escrito conforme a Guía de Referencia IV', nav: '/politica' },
    { numeral: '5.7', titulo: 'Constancias de difusión', estado: state.mediosDifusion.some(Boolean), desc: 'Evidencia de difusión de la política y medidas a los trabajadores', nav: '/politica' },
    { numeral: '5.5', titulo: 'Identificación de ATS (Guía I)', estado: counts.gi > 0, desc: `${counts.gi} de ${trabajadores.length} trabajadores evaluados`, nav: '/cuestionarios' },
    { numeral: '5.4/8.1', titulo: 'Medidas de prevención y control', estado: (pi.acciones || []).length > 0, desc: 'Acciones de prevención, mecanismos de queja, promoción de entorno favorable', nav: '/programa' },
    { numeral: '8.2', titulo: 'Programa de prevención (9 temas)', estado: (pi.acciones || []).length > 0, desc: 'Liderazgo, cargas, control, apoyo social, trabajo-familia, reconocimiento, violencia, comunicación, capacitación', nav: '/programa' },
  );

  // ── Centros 16-50 y 51+ ──
  if (obligaciones.nivel >= 2) {
    docs.push(
      { numeral: '5.2/7.1a', titulo: 'Identificación de FRP (Guía II)', estado: counts.gii > 0, desc: `${counts.gii} de ${trabajadores.length} trabajadores evaluados`, nav: '/cuestionarios' },
      { numeral: '5.6', titulo: 'Exámenes médicos y evaluaciones psicológicas', estado: false, desc: 'Para trabajadores expuestos a violencia o FRP con signos/síntomas', nav: null, info: 'Practicar a través de institución de seguridad social o privada, conforme a NOMs de la Secretaría de Salud.' },
      { numeral: '5.8', titulo: 'Registros de resultados y medidas', estado: counts.gi > 0 || counts.gii > 0, desc: 'Resultados de evaluación, medidas adoptadas, nombres de trabajadores evaluados', nav: '/resultados' },
      { numeral: '7.7', titulo: 'Informe de resultados (PDF)', estado: false, desc: 'Datos del centro, objetivo, método, resultados, conclusiones, recomendaciones, responsable con cédula', nav: '/resultados' },
      { numeral: '7.8', titulo: 'Resultados disponibles para consulta', estado: counts.gii > 0, desc: 'Los resultados deben estar disponibles para consulta de los trabajadores', nav: '/resultados', info: 'Se recomienda publicar un resumen general (sin datos individuales) en áreas comunes.' },
      { numeral: '7.9', titulo: 'Periodicidad de evaluación (cada 2 años)', estado: false, desc: 'La evaluación debe realizarse al menos cada dos años', nav: null, info: 'Registrar la fecha de evaluación y programar la siguiente.' },
      { numeral: '8.3-8.5', titulo: 'Programa de intervención (3 niveles)', estado: (pi.acciones || []).length > 0, desc: '1° Organizacional, 2° Grupal, 3° Individual (clínico/terapéutico)', nav: '/programa' },
    );
  }

  // ── Centros 51+ ──
  if (obligaciones.nivel >= 3) {
    docs.push(
      { numeral: '5.3/7.1b', titulo: 'Evaluación entorno organizacional (Guía III)', estado: counts.giii > 0, desc: `${counts.giii} de ${trabajadores.length} — puede aplicarse a muestra representativa`, nav: '/cuestionarios' },
      { numeral: '7.3', titulo: 'Evaluación del entorno organizacional', estado: counts.giii > 0, desc: 'Pertenencia, formación, responsabilidades, comunicación, cargas, reconocimiento', nav: '/resultados' },
    );
  }

  // Comunes
  docs.push(
    { numeral: 'Guía V', titulo: 'Fichas de datos de los trabajadores', estado: counts.fichas > 0, desc: `${counts.fichas} de ${trabajadores.length} completadas`, nav: '/cuestionarios' },
    { numeral: '', titulo: 'Consentimientos informados', estado: counts.consent > 0, desc: `${counts.consent} de ${trabajadores.length} trabajadores`, nav: '/cuestionarios' },
  );

  const completados = docs.filter(d => d.estado).length;
  const progreso = Math.round((completados / docs.length) * 100);

  return (
    <div>
      <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: t.textStrong, marginBottom: 4 }}>Evidencia documental</h1>
      <p style={{ color: t.textMuted, fontSize: '0.88rem', marginBottom: 4 }}>{centro.nombreCentro} · {obligaciones.label}</p>
      <p style={{ color: t.textFaint, fontSize: '0.78rem', marginBottom: 20 }}>Documentos requeridos para inspección STPS según tamaño del centro.</p>

      <div style={{ padding: 20, background: dark ? 'rgba(255,255,255,0.02)' : '#fff', border: `1px solid ${t.border}`, borderRadius: 14, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: t.textStrong }}>Progreso de evidencia</span>
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: t.textLink }}>{completados}/{docs.length} ({progreso}%)</span>
        </div>
        <div style={{ height: 8, background: dark ? 'rgba(255,255,255,0.06)' : '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progreso}%`, background: progreso === 100 ? '#10b981' : t.accent, borderRadius: 4, transition: 'width 0.5s ease' }} />
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {docs.map((doc, i) => (
          <div key={i} onClick={() => doc.nav && navigate(doc.nav)} style={{
            padding: '16px 20px', background: dark ? 'rgba(255,255,255,0.02)' : '#fff',
            border: `1px solid ${t.border}`, borderRadius: 12, cursor: doc.nav ? 'pointer' : 'default',
            borderLeft: `3px solid ${doc.estado ? '#10b981' : (dark ? '#555' : '#d1d5db')}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {doc.numeral && <span style={{ padding: '2px 8px', borderRadius: 5, fontSize: '0.65rem', fontWeight: 700, background: dark ? 'rgba(99,102,241,0.15)' : '#eef2ff', color: t.textLink }}>{doc.numeral}</span>}
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: t.textStrong }}>{doc.titulo}</span>
                </div>
                <div style={{ fontSize: '0.78rem', color: t.textMuted, marginTop: 4 }}>{doc.desc}</div>
                {doc.info && <div style={{ fontSize: '0.72rem', color: t.textFaint, marginTop: 4, fontStyle: 'italic', paddingLeft: 12, borderLeft: `2px solid ${t.border}` }}>{doc.info}</div>}
              </div>
              <span style={{
                padding: '4px 12px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 600, flexShrink: 0, marginLeft: 12,
                background: doc.estado ? 'rgba(16,185,129,0.1)' : (dark ? 'rgba(255,255,255,0.04)' : '#f3f4f6'),
                color: doc.estado ? '#10b981' : t.textFaint,
              }}>
                {doc.estado ? '✓ Completo' : 'Pendiente'}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
